import { Calendar, Watch, FileText, Wifi } from "lucide-react";

export type DataSource =
  | "manual"
  | "google_calendar" | "apple_calendar"
  | "apple_health" | "garmin" | "fitbit" | "wearable"
  | "notion" | "import"
  | "google_tasks" | "microsoft_todo" | "apple_reminders"
  | "spotify";

interface SourceBadgeProps {
  source: DataSource;
  size?: "xs" | "sm";
}

const SOURCE_CONFIG: Record<DataSource, {
  label: string; icon: React.ElementType; bg: string; text: string; border: string;
} | null> = {
  manual: null,
  google_calendar: { label: "Google Cal", icon: Calendar, bg: "hsl(214 89% 52% / 0.12)", text: "hsl(214 89% 65%)", border: "hsl(214 89% 52% / 0.30)" },
  apple_calendar:  { label: "Apple Cal",  icon: Calendar, bg: "hsl(0 0% 80% / 0.10)",    text: "hsl(0 0% 75%)",    border: "hsl(0 0% 70% / 0.25)" },
  apple_health:    { label: "Apple Health", icon: Watch,  bg: "hsl(340 72% 55% / 0.12)", text: "hsl(340 72% 68%)", border: "hsl(340 72% 55% / 0.30)" },
  garmin:          { label: "Garmin",      icon: Watch,   bg: "hsl(187 72% 50% / 0.10)", text: "hsl(187 72% 60%)", border: "hsl(187 72% 50% / 0.25)" },
  fitbit:          { label: "Fitbit",      icon: Watch,   bg: "hsl(174 60% 45% / 0.12)", text: "hsl(174 60% 58%)", border: "hsl(174 60% 45% / 0.28)" },
  wearable:        { label: "Wearable",    icon: Watch,   bg: "hsl(187 72% 50% / 0.10)", text: "hsl(187 72% 60%)", border: "hsl(187 72% 50% / 0.25)" },
  notion:          { label: "Notion",      icon: FileText, bg: "hsl(0 0% 80% / 0.08)",   text: "hsl(0 0% 72%)",    border: "hsl(0 0% 65% / 0.22)" },
  import:          { label: "Imported",    icon: Wifi,    bg: "hsl(45 90% 55% / 0.10)",  text: "hsl(45 90% 62%)",  border: "hsl(45 90% 55% / 0.25)" },
  google_tasks:    { label: "Google Tasks", icon: FileText, bg: "hsl(214 89% 52% / 0.12)", text: "hsl(214 89% 65%)", border: "hsl(214 89% 52% / 0.28)" },
  microsoft_todo:  { label: "Microsoft To Do", icon: FileText, bg: "hsl(214 89% 52% / 0.12)", text: "hsl(214 89% 65%)", border: "hsl(214 89% 52% / 0.28)" },
  apple_reminders: { label: "Reminders",   icon: FileText, bg: "hsl(0 0% 80% / 0.08)",   text: "hsl(0 0% 72%)",    border: "hsl(0 0% 65% / 0.22)" },
  spotify:         { label: "Spotify",     icon: Wifi,    bg: "hsl(141 72% 42% / 0.12)", text: "hsl(141 72% 55%)", border: "hsl(141 72% 42% / 0.28)" },
};

export const SourceBadge = ({ source, size = "xs" }: SourceBadgeProps) => {
  const config = SOURCE_CONFIG[source];
  if (!config) return null;

  const Icon = config.icon;
  const isXs = size === "xs";

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full font-display font-semibold tracking-wide"
      style={{
        background: config.bg,
        border: `1px solid ${config.border}`,
        color: config.text,
        fontSize: isXs ? "8px" : "10px",
        padding: isXs ? "2px 6px" : "3px 8px",
        letterSpacing: "0.12em",
      }}
    >
      <Icon size={isXs ? 8 : 10} />
      {config.label}
    </span>
  );
};
